(function () {
  'use strict';

  // How often to check whether the theme has finished applying the chosen size.
  const ADD_POLL_MS = 60;

  // If the submitted variant id never changes (re-picking the size that was
  // already selected), treat it as settled after this long.
  const VARIANT_ASSUMED_MS = 700;

  // Absolute ceiling on the wait, so a pending add can never linger and fire
  // long after the shopper moved on.
  const ADD_GIVE_UP_MS = 4000;

  class FloatingAtc extends HTMLElement {
    constructor() {
      super();

      this.section = this.closest('product-info');
      this.bar = this.querySelector('[data-floating-atc-button]');
      this.panel = this.querySelector('[data-floating-atc-panel]');
      this.backdrop = this.querySelector('[data-floating-atc-backdrop]');
      this.priceTarget = this.querySelector('[data-floating-atc-price]');
      this.textTarget = this.querySelector('[data-floating-atc-text]');

      this.hasOptions = this.hasAttribute('data-has-options');

      this.syncFromRealButton = this.syncFromRealButton.bind(this);
      this.onFormMutated = this.onFormMutated.bind(this);
      this.syncPrice = this.syncPrice.bind(this);
      this.setHeight = this.setHeight.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
    }

    connectedCallback() {
      if (!this.section || !this.realButton()) return;

      this.formWrapper = this.section.querySelector('[data-form-wrapper]');
      this.realPrice = this.section.querySelector('[id^="Price-"]');

      this.syncFromRealButton();
      this.syncPrice();
      this.observeSource();

      this.bar.addEventListener('click', this.handleBarClick.bind(this));
      this.addEventListener('click', this.handleOptionClick.bind(this));
      if (this.backdrop) this.backdrop.addEventListener('click', () => this.closePanel());
      document.addEventListener('keydown', this.onKeydown);
      window.addEventListener('resize', this.setHeight);

      this.watchFooter();

      this.classList.add('is-active');
      this.setHeight();
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKeydown);
      window.removeEventListener('resize', this.setHeight);
      clearInterval(this.addPoll);

      if (this.formObserver) this.formObserver.disconnect();
      if (this.priceObserver) this.priceObserver.disconnect();
      if (this.visibilityObserver) this.visibilityObserver.disconnect();
      if (this.footerObserver) this.footerObserver.disconnect();
    }

    // Always re-query: the theme can replace the form markup when the variant
    // changes, which would leave a cached reference pointing at a dead node.
    //
    // Scoped to the product's own form, because upsell and quick-add blocks
    // render their own [data-add-to-cart] inside this same section. While the
    // real button is briefly absent mid re-render, a section-wide lookup would
    // otherwise return one of those and add the wrong product.
    realButton() {
      if (!this.section) return null;

      const form = this.section.querySelector('[data-product-form]');
      const scope = form || this.section;

      return scope.querySelector('[data-add-to-cart]:not([data-quick-add-btn])');
    }

    observeSource() {
      // Watch the form wrapper rather than the button, so the observer survives
      // the button itself being swapped out on a variant change.
      const target = this.formWrapper || this.realButton();
      if (target) {
        this.formObserver = new MutationObserver(this.onFormMutated);
        this.formObserver.observe(target, {
          childList: true,
          subtree: true,
          characterData: true,
          attributes: true,
          attributeFilter: ['disabled', 'class'],
        });
      }

      if (this.realPrice) {
        this.priceObserver = new MutationObserver(this.syncPrice);
        this.priceObserver.observe(this.realPrice, {childList: true, subtree: true, characterData: true});
      }

      this.watchButtonVisibility();
    }

    // The bar exists to stand in for the real button while it is off screen,
    // so step aside whenever the real one is actually in view. An observer
    // keeps this off the scroll path entirely.
    watchButtonVisibility() {
      const button = this.realButton();
      if (!button || button === this.watchedButton) return;

      if (this.visibilityObserver) this.visibilityObserver.disconnect();
      if (!('IntersectionObserver' in window)) return;

      this.watchedButton = button;
      this.visibilityObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => this.setRealButtonVisible(entry.isIntersecting));
        },
        {threshold: 0}
      );
      this.visibilityObserver.observe(button);
    }

    setRealButtonVisible(isVisible) {
      this.hiddenByButton = isVisible;
      this.applyHidden();
    }

    // Pinned to the bottom of the viewport, the bar sits over whatever is down
    // there, which at the end of the page is the footer menu. Step aside as
    // soon as the footer appears rather than covering its links.
    watchFooter() {
      const footer = document.querySelector('.site-footer-wrapper') || document.querySelector('footer');
      if (!footer || !('IntersectionObserver' in window)) return;

      this.footerObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.hiddenByFooter = entry.isIntersecting;
            this.applyHidden();
          });
        },
        {threshold: 0}
      );
      this.footerObserver.observe(footer);
    }

    // Two independent reasons to stand down, so neither can undo the other.
    applyHidden() {
      const hide = Boolean(this.hiddenByButton || this.hiddenByFooter);
      this.classList.toggle('is-hidden', hide);
      if (hide) this.closePanel();
    }

    syncFromRealButton() {
      const button = this.realButton();
      if (!button) return;

      const sourceText = button.querySelector('[data-add-to-cart-text]');
      if (sourceText) {
        // The theme can fold the price into the button label; price is shown
        // separately on the bar, so strip it from the copied text.
        const clone = sourceText.cloneNode(true);
        clone.querySelectorAll('[data-product-price]').forEach((node) => node.remove());
        this.textTarget.textContent = clone.textContent.trim();
      }

      const disabled = button.hasAttribute('disabled');
      this.bar.toggleAttribute('disabled', disabled);
      this.classList.toggle('is-disabled', disabled);

      // The theme can swap the button out on a variant change; re-point the
      // visibility observer at whatever node is live now.
      this.watchButtonVisibility();
    }

    syncPrice() {
      if (!this.realPrice) return;
      this.priceTarget.innerHTML = this.realPrice.innerHTML;
    }

    // Measure the bar, not the host: every child here is position:fixed, so the
    // host itself collapses to zero height and would reserve no space at all.
    setHeight() {
      const bar = this.querySelector('.floating-atc__bar');
      if (!bar || !this.section) return;
      // Set on the section so both the panel and the section's own bottom
      // padding can read it; custom properties only inherit downward.
      this.section.style.setProperty('--floating-atc-height', `${bar.offsetHeight}px`);
    }

    onKeydown(event) {
      if (event.key === 'Escape') this.closePanel();
    }

    // Open state lives entirely in this one class, with CSS owning visibility.
    // Anything two-phase here (unhide now, animate next frame) can strand the
    // panel half-open if the frame is delayed.
    openPanel() {
      if (this.panel) this.classList.add('is-open');
    }

    closePanel() {
      this.classList.remove('is-open');
    }

    handleBarClick(event) {
      event.preventDefault();

      // On a product with variants the size panel stands between the tap and
      // the cart, so a size is always chosen deliberately.
      if (this.hasOptions && this.panel) {
        this.classList.contains('is-open') ? this.closePanel() : this.openPanel();
        return;
      }

      this.addToCart();
    }

    handleOptionClick(event) {
      const option = event.target.closest('[data-floating-atc-option]');
      if (!option) return;

      event.preventDefault();
      if (option.getAttribute('aria-disabled') === 'true') return;

      const input = this.findRealInput(option);
      if (!input) {
        // Unexpected picker markup - send them to the real selector rather
        // than silently doing nothing.
        this.closePanel();
        const picker = this.section.querySelector('variant-selects');
        if (picker) picker.scrollIntoView({behavior: 'smooth', block: 'center'});
        return;
      }

      // Deliberately not input.click(): that focuses the visually hidden radio
      // and the browser scrolls it into view, dragging the page down to the
      // real picker. VariantSelects listens for change, so set and dispatch.
      input.checked = true;
      input.dispatchEvent(new Event('change', {bubbles: true}));

      const group = option.closest('.floating-atc__options');
      if (group) {
        group.querySelectorAll('[data-floating-atc-option]').forEach((el) => el.classList.remove('is-selected'));
      }
      option.classList.add('is-selected');

      this.closePanel();

      // The tap that opened this panel was already an add to cart, so finish
      // the job once the theme has applied the variant.
      this.queueAdd(this.currentVariantId());
    }

    onFormMutated() {
      this.syncFromRealButton();
    }

    // The id the form will submit. Watching it change is how we know the theme
    // has finished applying the newly chosen size.
    currentVariantId() {
      const input = this.section.querySelector('input[name="id"]');
      return input ? input.value : null;
    }

    // Polled rather than driven by mutation events: the theme can replace the
    // form wholesale, which detaches any observer watching inside it, and a
    // dropped event here means a tap that silently does nothing.
    queueAdd(variantIdBefore) {
      this.cancelPendingAdd();

      this.pendingAdd = true;
      this.classList.add('is-busy');

      const startedAt = Date.now();
      this.addPoll = setInterval(() => this.pollAdd(variantIdBefore, startedAt), ADD_POLL_MS);
    }

    cancelPendingAdd() {
      this.pendingAdd = false;
      this.classList.remove('is-busy');
      clearInterval(this.addPoll);
    }

    pollAdd(variantIdBefore, startedAt) {
      if (!this.pendingAdd) {
        clearInterval(this.addPoll);
        return;
      }

      const elapsed = Date.now() - startedAt;
      const button = this.realButton();
      const expired = elapsed >= ADD_GIVE_UP_MS;

      if (!button) {
        // Mid re-render. Keep waiting unless we are out of time.
        if (expired) this.cancelPendingAdd();
        return;
      }

      if (button.hasAttribute('disabled')) {
        // Either still settling, or genuinely sold out in this size. Give it
        // until the ceiling, then leave it alone; the bar shows Sold out.
        if (expired) this.cancelPendingAdd();
        return;
      }

      // Don't add before the variant has actually switched, or we'd add the
      // previously selected size. Re-picking the current size never changes
      // the id, so treat a short grace period as settled too.
      const settled =
        variantIdBefore === null ||
        this.currentVariantId() !== variantIdBefore ||
        elapsed >= VARIANT_ASSUMED_MS;

      if (!settled && !expired) return;

      this.cancelPendingAdd();
      button.click();
    }

    addToCart() {
      const button = this.realButton();
      if (!button || button.hasAttribute('disabled')) return;
      button.click();
    }

    findRealInput(option) {
      const escape = window.CSS && window.CSS.escape ? window.CSS.escape : (str) => str.replace(/"/g, '\\"');

      // The option value id is what the theme itself keys variants on, and it
      // sidesteps the escaping differences between the button and swatch
      // pickers, so prefer it and only fall back to matching name and value.
      const valueId = option.getAttribute('data-option-value-id');
      if (valueId) {
        const byId = this.section.querySelector(`input[data-option-value-id="${escape(valueId)}"]`);
        if (byId) return byId;
      }

      const name = option.getAttribute('data-option-name');
      const value = option.getAttribute('data-option-value');
      if (!name || value === null) return null;

      return this.section.querySelector(`input[name="${escape(name)}"][value="${escape(value)}"]`);
    }
  }

  if (!customElements.get('floating-atc')) {
    customElements.define('floating-atc', FloatingAtc);
  }
})();
